/**
 * File upload constants for the MediaUploader component
 */

// File type configurations
export const FILE_TYPES = {
  image: {
    mimeTypes: [
      'image/jpeg',
      'image/png',
      'image/webp',
      'image/avif',
    ] as string[],
    extensions: ['.jpg', '.jpeg', '.png', '.webp', '.avif'] as string[],
    label: 'JPEG, PNG, WebP, or AVIF',
  },
  video: {
    mimeTypes: ['video/mp4', 'video/quicktime'] as string[],
    extensions: ['.mp4', '.mov'] as string[],
    label: 'MP4 or MOV',
  },
  pdf: {
    mimeTypes: ['application/pdf'] as string[],
    extensions: ['.pdf'] as string[],
    label: 'PDF',
  },
};

// Common file size limits
export const FILE_SIZE_LIMITS = {
  small: 5 * 1024 * 1024, // 5MB - for headshots, avatars
  medium: 10 * 1024 * 1024, // 10MB - for photos
  large: 50 * 1024 * 1024, // 50MB - for videos, documents
};

// Accept types for react-dropzone
export type AcceptType = Record<string, string[]>;

// Default configurations for common use cases
export const UPLOADER_PRESETS: Record<
  string,
  {
    allowedTypes: string[];
    maxFileSize: number;
    maxFiles: number;
    accept: AcceptType;
    hint: string;
  }
> = {
  headshot: {
    allowedTypes: FILE_TYPES.image.mimeTypes,
    maxFileSize: 15 * 1024 * 1024, // 15MB — high-res PNG/JPEG headshots
    maxFiles: 1,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/webp': ['.webp'],
      'image/avif': ['.avif'],
    },
    // Size is appended dynamically by MediaUploader from maxFileSize.
    hint: 'JPEG, PNG, WebP or AVIF',
  },
  photoGallery: {
    allowedTypes: FILE_TYPES.image.mimeTypes,
    maxFileSize: 20 * 1024 * 1024, // 20MB — high-res gallery photos
    maxFiles: 10,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/webp': ['.webp'],
      'image/avif': ['.avif'],
    },
    // Size is appended dynamically by MediaUploader from maxFileSize.
    hint: 'JPEG, PNG, WebP or AVIF',
  },
  venueDocument: {
    allowedTypes: [
      ...FILE_TYPES.image.mimeTypes,
      ...FILE_TYPES.video.mimeTypes,
      ...FILE_TYPES.pdf.mimeTypes,
    ],
    maxFileSize: FILE_SIZE_LIMITS.large,
    maxFiles: 20,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/webp': ['.webp'],
      'image/avif': ['.avif'],
      'video/mp4': ['.mp4'],
      'video/quicktime': ['.mov'],
      'application/pdf': ['.pdf'],
    },
    hint: 'Photos, videos, or PDFs',
  },
  reimbursementReceipt: {
    allowedTypes: [...FILE_TYPES.image.mimeTypes, ...FILE_TYPES.pdf.mimeTypes],
    maxFileSize: FILE_SIZE_LIMITS.medium,
    maxFiles: 10,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'application/pdf': ['.pdf'],
    },
    hint: 'PDF, JPEG, or PNG',
  },
};

export type FileType = keyof typeof FILE_TYPES;
export type UploaderPreset = keyof typeof UPLOADER_PRESETS;

/**
 * Canonical per-MIME → file extensions + short human label. Single source for
 * deriving a native `accept` map and a hint from an arbitrary `allowedTypes`
 * MIME list (e.g. a stored `application_structure` field that passes
 * `validation.fileTypes` but no preset). Keep aligned with the `accept` maps in
 * UPLOADER_PRESETS above and the MIME allowlists in `normalize-file-types.ts`.
 */
const MIME_DESCRIPTORS: Record<
  string,
  { extensions: string[]; label: string }
> = {
  'application/pdf': { extensions: ['.pdf'], label: 'PDF' },
  'image/jpeg': { extensions: ['.jpg', '.jpeg'], label: 'JPEG' },
  'image/jpg': { extensions: ['.jpg', '.jpeg'], label: 'JPEG' },
  'image/png': { extensions: ['.png'], label: 'PNG' },
  'image/webp': { extensions: ['.webp'], label: 'WebP' },
  'image/avif': { extensions: ['.avif'], label: 'AVIF' },
  'video/mp4': { extensions: ['.mp4'], label: 'MP4' },
  'video/quicktime': { extensions: ['.mov'], label: 'MOV' },
  'video/webm': { extensions: ['.webm'], label: 'WebM' },
};

const WILDCARD_LABELS: Record<string, string> = {
  'image/*': 'Images',
  'video/*': 'Videos',
  'audio/*': 'Audio',
};

/**
 * Builds a react-dropzone `accept` map from a list of MIME types so the native
 * file picker greys out disallowed files. Returns undefined for an empty list
 * (react-dropzone treats `{}` as "accept nothing"). Legacy semantic labels
 * (values without a `/`) are skipped defensively — callers should normalize via
 * `normalizeFileTypes` first. Unknown MIME types still constrain the picker by
 * their MIME key with no extension hint.
 */
export function buildAcceptFromMimeTypes(
  mimeTypes: string[] | undefined,
): AcceptType | undefined {
  if (!mimeTypes || mimeTypes.length === 0) return undefined;
  const accept: AcceptType = {};
  for (const mime of mimeTypes) {
    if (!mime || !mime.includes('/')) continue;
    accept[mime] = MIME_DESCRIPTORS[mime]?.extensions ?? [];
  }
  return Object.keys(accept).length > 0 ? accept : undefined;
}

/**
 * Every file extension the given MIME types are known by, deduped and
 * lowercased (e.g. `['application/pdf']` → `['.pdf']`).
 *
 * Exists because a browser's reported `file.type` is not reliable: with no
 * OS-level mapping for an extension it reports `application/octet-stream` (or
 * nothing at all) for a perfectly valid file. The extension is then the only
 * signal left, so both the picker's `accept` and the client-side type check
 * fall back to it — see `validateFile` and BAL-747.
 */
export function extensionsForMimeTypes(
  mimeTypes: string[] | undefined,
): string[] {
  const extensions: string[] = [];
  for (const mime of mimeTypes ?? []) {
    for (const extension of MIME_DESCRIPTORS[mime]?.extensions ?? []) {
      const normalized = extension.toLowerCase();
      if (!extensions.includes(normalized)) extensions.push(normalized);
    }
  }
  return extensions;
}

/**
 * `accept` for a plain `<input type="file">`: the MIME types plus every
 * extension they are known by.
 *
 * The extensions are not redundant. A browser with no mapping for
 * `application/pdf` matches nothing on the MIME alone and greys out every PDF
 * in the dialog, so the user cannot even reach the upload. (Components built on
 * react-dropzone get this already via `buildAcceptFromMimeTypes`, whose map
 * carries the same extensions — this is the equivalent for raw inputs.)
 */
export function buildAcceptAttribute(
  mimeTypes: string[] | undefined,
): string | undefined {
  if (!mimeTypes || mimeTypes.length === 0) return undefined;
  const parts = [...mimeTypes.filter(Boolean)];
  for (const extension of extensionsForMimeTypes(mimeTypes)) {
    if (!parts.includes(extension)) parts.push(extension);
  }
  return parts.length > 0 ? parts.join(',') : undefined;
}

/**
 * Renders a list of MIME types as a human-readable allowed-formats hint, e.g.
 * `['application/pdf']` → "PDF", `['image/jpeg','image/png','image/webp']` →
 * "JPEG, PNG or WebP". Dedupes labels (image/jpeg + image/jpg → one "JPEG"),
 * handles `image/*` / `video/*` wildcards, and falls back to the uppercased
 * subtype for unknown types. Returns undefined for an empty list.
 */
export function describeMimeTypes(
  mimeTypes: string[] | undefined,
): string | undefined {
  if (!mimeTypes || mimeTypes.length === 0) return undefined;
  const labels: string[] = [];
  for (const mime of mimeTypes) {
    if (!mime) continue;
    const label =
      WILDCARD_LABELS[mime] ??
      MIME_DESCRIPTORS[mime]?.label ??
      mime.split('/').pop()?.toUpperCase();
    if (label && !labels.includes(label)) labels.push(label);
  }
  if (labels.length === 0) return undefined;
  if (labels.length === 1) return labels[0];
  return `${labels.slice(0, -1).join(', ')} or ${labels[labels.length - 1]}`;
}
